/**
 * **The Arabis-class sloop as the sim sees her** — her published dimensions,
 * her speed, the radius she turns in, and the five mountings on her deck as
 * `GunMountSpec` records in the body frame.
 *
 * The model, the rig and the contract this file codes against are
 * [ship-design.md](../../../../docs/engineering/ship-design.md); nothing here
 * re-derives them. Two of that document's sections are the whole of this file:
 * §2's four published figures, and §4.1's seventeen-node rig table.
 *
 * ## The frames line up, which is why there is no conversion in here
 *
 * ship-design.md §4 states the loaded `.glb`'s frame as **+X starboard, +Y up,
 * bow −Z**. That is `math3d.ts`'s body frame exactly — `BODY_RIGHT`, `BODY_UP`,
 * `BODY_FORWARD`. So a rig node's *world origin* column in §4.1 is a
 * `GunMountSpec.bodyOffsetM` with no axis map applied, and the renderer can put
 * a muzzle flash at `position + rotate(orientation, bodyOffsetM)` using the same
 * arithmetic `geometry.ts`'s `muzzlePosition` already does for an aeroplane.
 *
 * The airship had to work the other way round (`airship-guns.ts` reads `MG.DAT`
 * model units and divides out `MODEL_UNITS_PER_METRE`) because its mounts came
 * out of a file. These came out of a generator that was written in metres.
 *
 * ## What is cited and what is not, restated at the numbers
 *
 * **Cited** (ship-design.md §2, Wikipedia *Arabis-class sloop*): length overall
 * 81.61 m, beam 10.21 m, draught 3.58 m, **17 knots**, and the armament — one
 * 4.7 in gun and two 3-pounder AA. Those five are the class's own.
 *
 * **Ours, and marked as such below**: the turn radius, the machine guns'
 * belts, and every arc limit — §4.1's travel column is *stated, not measured*,
 * and this file is where the statement finally becomes a constraint the sim
 * obeys. Nothing in the geometry stops a mount training through the bridge; the
 * arcs here are what stop it.
 *
 * **Borrowed, deliberately**: the machine guns' effectiveness, their engagement
 * range and their rate of fire are the airship's, which is to say `MG.DAT`'s.
 * They are the only recovered numbers this port has for a *defensive machine
 * gun on a thing that is not an aeroplane*, and a Maxim in a splinter tub is
 * the same weapon in the same role as a Maxim on a gasbag's crown. Borrowing a
 * recovered number beats inventing a fresh one, and the import makes the
 * provenance structural rather than a sentence.
 */

import type { GunMountSpec, GunSpec } from "../flight/guns.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { BAM16_PER_TURN } from "../flight/units.ts";
import {
  AIRSHIP_RATE_OF_FIRE_FRACTION,
  ZEPPELIN_ENGAGEMENT_RANGE_M,
  ZEPPELIN_GUN_EFFECTIVENESS,
} from "./airship-guns.ts";
import type { FlakClass } from "./flak-shell.ts";

/** Length overall, metres. CITED — ship-design.md §2, and asserted against the built `.glb` to the centimetre. */
export const SHIP_LENGTH_OVERALL_M = 81.61;

/** Extreme beam, metres. CITED. */
export const SHIP_BEAM_M = 10.21;

/** Draught, metres. CITED. The model's origin is on the waterline, so nothing here offsets by it. */
export const SHIP_DRAUGHT_M = 3.58;

/** One knot in metres per second. */
const METRES_PER_SECOND_PER_KNOT = 0.514444;

/**
 * Service speed, metres per second — the class's published **17 knots**.
 *
 * She makes it all the time. A convoy escort on patrol is not loitering and is
 * not working up to full power; one speed for the whole sortie is what makes
 * her track a pure function of the clock (`ship-patrol.ts`), and a sloop that
 * accelerated would need an accumulator in the hashed state to say how far she
 * had got.
 */
export const SHIP_SPEED_MPS = 17 * METRES_PER_SECOND_PER_KNOT;

/**
 * The tightest circle she can be made to steam, metres — **hard rudder**, and
 * the physical floor every other turning number here is measured against.
 * **OURS**, from a published rule rather than from trials data.
 *
 * No lines plan and no Admiralty turning trial survives for this class, so the
 * figure is the standards body's own ceiling applied to a length that *is*
 * cited. IMO's manoeuvrability standard (Resolution MSC.137(76), the
 * turning-circle criterion) requires a ship's **tactical diameter** — the
 * lateral distance covered in a 180° turn at hard over — to be **no more than
 * five ship lengths**, and puts the advance at no more than 4.5; the customary
 * seamanship figure for a single-screw steamer is four lengths. Four lengths of
 * tactical diameter is a radius of two, which is this constant. Expressed as a
 * multiple of `SHIP_LENGTH_OVERALL_M` rather than as a bare metre count, so it
 * cannot drift away from the hull it belongs to.
 *
 * **Nothing steams this circle.** It is the limit a patrol must stay inside,
 * not the turn a patrol uses — see `SHIP_CRUISE_TURN_RADIUS_M` for why, and
 * `ship-patrol.ts` for the curve that actually gets drawn.
 */
export const SHIP_HARD_TURN_RADIUS_M = 2 * SHIP_LENGTH_OVERALL_M;

/**
 * The radius her **patrol** turns are steamed at, metres — a cruising helm, not
 * a hard-over one. **OURS**, and the correction of record for the user's report
 * of 2026-09-01 that her turns looked too tight.
 *
 * The shipped patrol used `SHIP_HARD_TURN_RADIUS_M` itself, which is the wrong
 * *kind* of number for the job: a hard-over turning circle is what a ship does
 * to avoid a collision, and a convoy escort altering course on patrol puts over
 * a fraction of that helm. The steady turning radius of a hull is inversely
 * proportional to rudder angle (`R = U/r`, and the yaw rate `r` is what the
 * rudder sets), so a helm of half the hard-over angle steams **twice** the
 * radius. Royal Navy hard over of the period is 35°; half of it is an ordinary
 * cruising helm, so the patrol radius is twice the hard-over one — **four ship
 * lengths, 326.4 m**, and the tactical diameter of the turn she actually makes
 * is eight lengths against the five that IMO caps a *hard-over* trial at. The
 * two numbers do not contradict: one is a limit at full helm and the other is a
 * gentler turn well inside it.
 *
 * At `SHIP_SPEED_MPS` this is a peak rate of turn of **1.53 °/s** against the
 * old 3.07, and the 180° takes 136 s against 59 — and even the peak is only
 * reached in the middle of the turn, because the helm goes over over
 * `SHIP_TURN_EASE_M` of track rather than in one tick.
 */
export const SHIP_CRUISE_TURN_RADIUS_M = 2 * SHIP_HARD_TURN_RADIUS_M;

/**
 * The track over which the helm goes over and the turn develops, metres — the
 * length of the spiral that joins a straight to a steady turn. **OURS**, summed
 * from two citable pieces.
 *
 * A real ship does not step from straight to a steady rate of turn, and the
 * shipped patrol did: her curvature jumped from zero to `1/R` in one tick at
 * each end of every straight. Two things stop that happening at sea, and both
 * have a number:
 *
 * - **The steering gear has a speed.** SOLAS II-1/29 requires main steering
 *   gear to put the rudder from 35° on one side to 35° on the other in not more
 *   than 28 seconds — 0.4 s a degree — so reaching a 17.5° cruising helm from
 *   amidships takes about **7 s**, which at `SHIP_SPEED_MPS` is 61 m of track.
 * - **The hull then has to answer it.** The standing seamanship description of
 *   a turning circle is that a ship carries on nearly straight for about **one
 *   ship length** after the helm goes over before the turn takes hold — 81.6 m.
 *
 * Sixty-one plus eighty-two is a hundred and forty-three, which this rounds to
 * **two ship lengths**, for the same reason the radii are multiples of the
 * hull: a length is the unit the ship's own manoeuvring is described in, and a
 * bare metre count would drift away from her. At `SHIP_CRUISE_TURN_RADIUS_M`
 * each spiral turns `E / 2R` = **14.3°**, so a 180° alteration is 14° of
 * gathering way, 151° of steady turn and 14° of easing out.
 *
 * The port models the *geometry* of that transition and not its hydrodynamics:
 * there is no Nomoto model and no drift angle here, and nothing in the recovered
 * data would calibrate one.
 */
export const SHIP_TURN_EASE_M = 2 * SHIP_LENGTH_OVERALL_M;

/** Peak radians per second on a cruising turn — reached only at its middle. Derived; nothing sets it. */
export const SHIP_CRUISE_TURN_RATE_RAD_PER_S = SHIP_SPEED_MPS / SHIP_CRUISE_TURN_RADIUS_M;

/** Radians per second at `SHIP_SPEED_MPS` at hard rudder — the rate nothing this port draws ever reaches. */
export const SHIP_HARD_TURN_RATE_RAD_PER_S = SHIP_SPEED_MPS / SHIP_HARD_TURN_RADIUS_M;

/**
 * The radius gunnery and collision treat her as, metres — half her length.
 *
 * A ship is not a sphere and this port has no other shape for a target, so the
 * honest choice is the one that makes her *hittable along her length*: a round
 * aimed at the funnel and one aimed at the stern are both aimed at the ship.
 * `target-profiles.ts` turns it into `targetSizeRaw` through the same
 * 0.035 m/unit the fleet uses.
 */
export const SHIP_FOOTPRINT_RADIUS_M = SHIP_LENGTH_OVERALL_M / 2;

/**
 * Which of `flak-shell.ts`'s two classes her 3-pounders are.
 *
 * **Light**, and it is a real reading rather than a default: `FLAK_ENVELOPE`
 * gives light 762 m of horizontal range against heavy's 1905, and a 47 mm
 * Hotchkiss on a high-angle mounting is a close-in weapon — the field army's
 * heavy class is a 77 mm on a proper carriage. A ship that engaged at 1905 m
 * would dominate the map she sits in.
 */
export const SHIP_FLAK_CLASS: FlakClass = "light";

/**
 * Guns in the ship's anti-aircraft battery — **two**, one per 3-pounder.
 *
 * `flak.ts` never reads `FLAK_SLOTS_PER_BATTERY`; it iterates whatever slots the
 * battery record carries, so a two-slot battery needs no change there. Two is
 * also what makes her a threat rather than an execution: `stepFlak`'s
 * ranged-in gun knocks every *other* gun of the battery off its count, so at
 * most one of the two is ever shooting well, and the other is starting again.
 */
export const SHIP_FLAK_GUNS = 2;

/** Machine-gun stations — two Maxims in splinter tubs on the bridge deck (ship-design.md §3.3). */
export const SHIP_MG_STATIONS = 2;

/**
 * Rounds in each Maxim's belt at the start of a sortie. **OURS.**
 *
 * Not the airship's 120: that number is `pobj.c`'s own seeding of a gasbag's
 * four ammunition boxes, and a warship's magazine is not four boxes. 500 is the
 * fleet's own `roundsPerGun` — about half a minute of firing per station if
 * anything stayed in the arc that long, which nothing does. It is finite for
 * the same reason the airship's is: a ship that spends her belts on the first
 * pass has less for the second, and nothing reloads her.
 */
export const SHIP_MG_ROUNDS_PER_STATION = 500;

/**
 * Whether the 4.7 in gun may fire. **`false`, and it is a decision rather than
 * an unimplemented case** (user, 2026-08-31: *"we may use at some point"*).
 *
 * The mount still trains — `ship-pass.ts` reports its bearing so the renderer
 * can swing `MAIN_YAW`/`MAIN_PITCH` toward whatever the battery is engaging —
 * because a gunhouse that never moves while the flak beside it hammers away
 * reads as broken. What it does not do is put anything in the air, spend a
 * round, or draw from any stream. Read this constant rather than deleting the
 * mount when the day comes.
 */
export const SHIP_MAIN_GUN_FIRES = false;

/** Radians to the BAM16 counts a `GunMountSpec`'s raw columns are expressed in. */
function bamOf(radians: number): number {
  return Math.round((radians / (2 * Math.PI)) * BAM16_PER_TURN);
}

const DEG = Math.PI / 180;

/**
 * The metres-per-unit `GunMountSpec`'s three raw position columns are written
 * in. It is `aircraft-model-frame.ts`'s `MODEL_UNITS_PER_METRE` inverted, and it
 * is here only so a ship mount prints in the same units an aeroplane's does when
 * a debug view lists them side by side.
 */
const RAW_UNITS_PER_METRE = 1 / 0.0153846;

/** One mounting, from its rig-node origin in metres and its arcs in degrees. */
interface MountSeed {
  readonly name: string;
  readonly offsetM: Vec3;
  readonly elevationFromDeg: number;
  readonly elevationToDeg: number;
  /** Half-width of the training arc either side of the bow, degrees. 180 is all round. */
  readonly azimuthHalfDeg: number;
}

/**
 * The five mountings, in `ship-design.md` §4.1's own order, at the **world
 * origin** column of that table — the position of the *pitch* node, which is
 * where the barrel is hinged and therefore where the muzzle swings from.
 *
 * §4.3 warns that the main gun's trunnion sits 0.55 m forward of its training
 * axis, so the muzzle is not `pivot + direction × length`. That warning is for
 * whoever *draws* it: the renderer must read the muzzle off `MAIN_PITCH`'s own
 * `matrixWorld`. The sim does not care, because the sim never fires this gun
 * (`SHIP_MAIN_GUN_FIRES`) and a bearing has no length.
 *
 * The arcs are ship-design.md's travel column, which that document is careful to
 * mark as stated rather than measured. They become real here.
 */
const MOUNT_SEEDS: readonly MountSeed[] = [
  { name: "FLAK1", offsetM: { x: 2.15, y: 5.76, z: 7.2 }, elevationFromDeg: -5, elevationToDeg: 80, azimuthHalfDeg: 180 },
  { name: "FLAK2", offsetM: { x: -2.15, y: 5.76, z: 7.2 }, elevationFromDeg: -5, elevationToDeg: 80, azimuthHalfDeg: 180 },
  { name: "MG1", offsetM: { x: 2.8, y: 6.44, z: -7.4 }, elevationFromDeg: -10, elevationToDeg: 70, azimuthHalfDeg: 180 },
  { name: "MG2", offsetM: { x: -2.8, y: 6.44, z: -7.4 }, elevationFromDeg: -10, elevationToDeg: 70, azimuthHalfDeg: 180 },
  { name: "MAIN", offsetM: { x: 0, y: 5.416, z: -25.55 }, elevationFromDeg: -5, elevationToDeg: 30, azimuthHalfDeg: 150 },
];

function buildMount(seed: MountSeed, index: number): GunMountSpec {
  const elevationFromRad = seed.elevationFromDeg * DEG;
  const elevationToRad = seed.elevationToDeg * DEG;
  // An all-round mount is expressed as the full (−π, π] interval rather than as
  // a wrapping arc, so `withinArc`'s `from <= to` branch takes every azimuth
  // `traverseOf` can produce and `wrapsRear` is honestly false.
  const azimuthToRad = seed.azimuthHalfDeg * DEG;
  const azimuthFromRad = -azimuthToRad;
  return {
    index,
    // Mount class 6 is the flexible-gunner class every `MG.DAT` station carries;
    // a pintle, a high-angle mounting and a training gunhouse are all one barrel
    // a man swings, which is what the class means.
    mountClassRaw: 6,
    barrels: 1,
    rightUnits: Math.round(seed.offsetM.x * RAW_UNITS_PER_METRE),
    forwardUnits: Math.round(-seed.offsetM.z * RAW_UNITS_PER_METRE),
    upUnits: Math.round(seed.offsetM.y * RAW_UNITS_PER_METRE),
    bodyOffsetM: seed.offsetM,
    verticalFromBam: bamOf(elevationFromRad),
    verticalToBam: bamOf(elevationToRad),
    horizontalFromBam: bamOf(azimuthFromRad),
    horizontalToBam: bamOf(azimuthToRad),
    elevationFromRad,
    elevationToRad,
    azimuthFromRad,
    azimuthToRad,
    wrapsRear: false,
    flexible: true,
  };
}

/** Every mounting on the ship, in `MOUNT_SEEDS` order. The renderer's rig node names are the seeds' own. */
export const SHIP_MOUNTS: readonly GunMountSpec[] = MOUNT_SEEDS.map(buildMount);

/** The rig-node prefix each entry of `SHIP_MOUNTS` drives — `FLAK1` means `FLAK1_YAW` and `FLAK1_PITCH`. */
export const SHIP_MOUNT_NAMES: readonly string[] = MOUNT_SEEDS.map((seed) => seed.name);

/** Indices into `SHIP_MOUNTS`. Named once, because three modules count on the order. */
export const SHIP_FLAK_MOUNT_INDICES: readonly number[] = [0, 1];
export const SHIP_MG_MOUNT_INDICES: readonly number[] = [2, 3];
export const SHIP_MAIN_MOUNT_INDEX = 4;

/** Metres above the waterline the two 90 cm projectors' drums sit, and where along the ship. ship-design.md §4.1. */
export const SHIP_LIGHT_OFFSETS_M: readonly Vec3[] = [
  { x: 0, y: 9.32, z: -10.6 },
  { x: 0, y: 6.52, z: 10.8 },
];

/**
 * Metres from her own origin to the 3-pounders' mounting — what the flak
 * battery's emplacement is offset by so a burst is thrown from the guns rather
 * than from the funnel.
 *
 * The mean of the two 3-pounder pitch nodes, which is on the centreline abaft
 * the funnel. `flak.ts` gives a battery one position for five guns and this ship
 * has two, 4.3 m apart; at the 762 m the light class engages at, that separation
 * is a fifth of a degree.
 */
export const SHIP_BATTERY_OFFSET_M: Vec3 = { x: 0, y: 5.76, z: 7.2 };

/**
 * The ceiling on everything the ship's Maxims do, metres — **the user's own
 * number** (2026-09-01: *"3,000 feet max"*), and the one figure in this file
 * that arrived as a request rather than out of a file or a citation.
 *
 * **914.4 and not 914**, because at four figures the same distance is three
 * statements at once and all three are exact: 3,000 ft is 1,000 yd is 914.4 m.
 * Rounding it to the metre would have made the constant 1.3 ft short of the
 * number the user actually said, for no gain.
 *
 * It is also the *defensible* number, which is why it was taken as stated
 * rather than argued down: a Maxim's practical anti-aircraft envelope is the
 * customary thousand yards, past which a rifle-calibre round arrives too slow
 * and too scattered to be worth the belt. **STATED, not recovered** — nothing in
 * the shipped data carries an AA envelope for a machine gun.
 *
 * **What it binds, measured rather than assumed.** On the day it landed it
 * bound *nothing about her fire*: `MG.DAT`'s own engagement range is 685.8 m
 * and the port's `gunMaxRangeM` is 700 m, so a round from her already ceased to
 * exist **214 m inside** this cap and `ballistics.ts` scored it zero from 700 m
 * out. It is written as a `min` anyway, because a ceiling that only holds while
 * somebody else's constant stays small is not a ceiling: the day a lane re-reads
 * `ZEPPELIN_BULLET_GLOBALS` or retunes the airship's envelope, the ship cannot
 * follow it past the number the user named.
 *
 * Where it binds **today** is the mountings. `ship-pass.ts` tracks out to this
 * range and opens fire only inside `SHIP_MG_ENGAGEMENT_RANGE_M`, because a
 * gunner is on his target before he shoots — so the cap is the outer edge of
 * everything a Maxim does, and the 228.6 m between the two is the margin he
 * follows through.
 */
export const SHIP_MG_RANGE_CAP_M = 914.4;

/**
 * How far the machine guns will engage, metres — `MG.DAT` `+0`, borrowed (see
 * the module note), under the ship's own `SHIP_MG_RANGE_CAP_M`.
 *
 * **685.8 m** today, because the borrowed envelope is the tighter of the two.
 * The `min` is what makes the cap a property of this ship rather than a comment
 * about the airship's current number.
 */
export const SHIP_MG_ENGAGEMENT_RANGE_M = Math.min(ZEPPELIN_ENGAGEMENT_RANGE_M, SHIP_MG_RANGE_CAP_M);

/** Damage per round from a ship's Maxim — `MG.DAT` `0x1A`, borrowed. */
export const SHIP_MG_EFFECTIVENESS = ZEPPELIN_GUN_EFFECTIVENESS;

/**
 * The two Maxims as one `GunSpec`, the same record an aeroplane's guns and an
 * airship's stations are described by — so `traverse.ts` needs no idea that the
 * thing holding it floats.
 *
 * `hasFixedGun: false` because nothing on this ship is boresighted: every mount
 * is a man swinging a barrel. With no fixed gun, a station that cannot be
 * brought onto anything simply puts nothing up, which is how the funnel's
 * shadow (§9.3 of ship-design.md, left open there) ends up costing the guns
 * nothing here: this port models the arc and not the obstruction, and says so
 * rather than pretending a bridge-wing Maxim can see through a boiler casing.
 */
export function shipMachineGunSpec(roundsPerSecond: number): GunSpec {
  const mounts = SHIP_MG_MOUNT_INDICES.map((index) => SHIP_MOUNTS[index]!);
  return {
    positions: mounts.length,
    mounts,
    forwardMount: mounts[0]!,
    hasFixedGun: false,
    barrels: 1,
    damageRaw: SHIP_MG_EFFECTIVENESS,
    jamThresholdRaw: 40,
    jamThresholdTable: [40, 20, 30],
    roundsPerGun: SHIP_MG_ROUNDS_PER_STATION,
    roundsPerSecond: roundsPerSecond * AIRSHIP_RATE_OF_FIRE_FRACTION,
  };
}

/** The belts a ship spawns with, one per Maxim. */
export function shipStationBelts(): readonly number[] {
  return SHIP_MG_MOUNT_INDICES.map(() => SHIP_MG_ROUNDS_PER_STATION);
}
